import { auxContainer, h1 } from "app/common/components/Containers"
import { NortherPageProps } from "app/common/components/NortherPageProps"
import { StructuredText } from "app/common/components/StructuredText"
import { justThrow } from "app/common/fp-ts"
import { logException } from "app/common/logging"
import { bindCommon } from "app/common/next"
import { UrlMgr } from "app/common/routes"
import { createSeoMetadata } from "app/common/seo/seoEssentials"
import { getStaticPropsResult } from "app/common/ui/next"
import { UnTaskEither } from "app/common/utilities/types"
import { toTitleString } from "app/common/web"
import BlogLayout from "app/layouts/BlogLayout"
import { getTou } from "app/terms-of-use/queries/getTermsOfUse"
import { BlitzPage, GetStaticProps } from "blitz"
import { LocaleD } from "db"
import { option, task, taskEither } from "fp-ts"
import { pipe } from "fp-ts/function"
import useTranslation from "next-translate/useTranslation"

interface TermsOfUseProps extends NortherPageProps {
  locale: LocaleD
  termsOfuse: UnTaskEither<ReturnType<typeof getTou>>
}

// ts-prune-ignore-next
export const getStaticProps: GetStaticProps<TermsOfUseProps> = ({ locale }) =>
  pipe(
    taskEither.Do,
    bindCommon(locale),
    taskEither.apS("termsOfuse", getTou()),
    taskEither.bind("seoMetadata", ({ locale, ogLocale, t }) =>
      pipe(
        {
          canonicalUrl: UrlMgr.TermsOfUse({ locale }),
          metaDescription: `Norther Terms of Use`,
          openGraphMetadata: {
            description: `Norther Terms of Use`,
            locale: ogLocale,
            siteName: "Norther",
            title: toTitleString([t("terms-of-use"), t("buy-local-buy-canadian"), `Norther`]),
            type: option.none,
            url: UrlMgr.TermsOfUse({ locale }),
          },
          title: toTitleString([t("terms-of-use"), t("buy-local-buy-canadian"), `Norther`]),
          urlMap: {
            en_ca: UrlMgr.TermsOfUse(),
            fr_ca: UrlMgr.TermsOfUse({ locale: LocaleD.fr_ca }),
            "x-default": UrlMgr.TermsOfUse(),
          },
        },
        createSeoMetadata,
        taskEither.of
      )
    ),
    taskEither.bimap(logException, getStaticPropsResult),
    taskEither.fold(justThrow, task.of)
  )()

const TermsOfUse: BlitzPage<TermsOfUseProps> = ({ locale, termsOfuse }) => {
  const { t } = useTranslation("common")

  return (
    <div className={auxContainer}>
      <h1 className={h1}>{t("terms-of-use")}</h1>

      <StructuredText data={termsOfuse[locale].content} entities={{ brands: [] }} />
    </div>
  )
}

TermsOfUse.suppressFirstRenderFlicker = true

TermsOfUse.getLayout = BlogLayout.getLayout

// ts-prune-ignore-next
export default TermsOfUse
